import { useEffect, useId, useRef, useState } from "react";
import { Calendar } from "lucide-react";
import clsx from "clsx";
import { BookingDateCalendar } from "@/components/ui/BookingDateCalendar";
import { clampDateToMin, formatIsoDateShort } from "@/lib/bookingDates";

export interface BookingDateFieldProps {
  id: string;
  label: string;
  value: string;
  min?: string;
  max?: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  /** Label nur für Screenreader, das Feld bleibt einzeilig. */
  compact?: boolean;
}

/**
 * Datumsfeld mit eigenem Kalender und deutscher Anzeige.
 * Der native Picker bleibt in Dialogen geschlossen bzw. ignoriert min/max.
 */
export function BookingDateField({
  id,
  label,
  value,
  min,
  max,
  onChange,
  required,
  disabled,
  compact = false,
}: BookingDateFieldProps) {
  const labelId = useId();
  const fieldRef = useRef<HTMLDivElement>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    if (!calendarOpen) return;
    function onPointerDown(event: PointerEvent) {
      if (!fieldRef.current?.contains(event.target as Node)) {
        setCalendarOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [calendarOpen]);

  function applyDate(next: string) {
    if (!next) {
      onChange("");
      return;
    }
    let iso = next;
    if (min) iso = clampDateToMin(iso, min);
    if (max && iso > max) iso = max;
    onChange(iso);
  }

  function handleCalendarSelect(next: string) {
    applyDate(next);
    setCalendarOpen(false);
  }

  return (
    <label htmlFor={id} id={labelId} className={clsx("block min-w-0 text-sm", compact && "w-[9.75rem]")}>
      <span className={compact ? "sr-only" : "mb-1 block text-label-secondary"}>{label}</span>
      <div
        ref={fieldRef}
        className={clsx(
          "eh-date-field",
          compact && "eh-date-field--compact",
          disabled && "eh-date-field--disabled",
          calendarOpen && "eh-date-field--open",
        )}
      >
        <button
          id={id}
          type="button"
          className="eh-date-field__trigger"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={calendarOpen}
          aria-required={required || undefined}
          aria-labelledby={labelId}
          onClick={() => {
            if (!disabled) setCalendarOpen((open) => !open);
          }}
        >
          <span
            className={clsx(
              "eh-date-field__display eh-input",
              !value && "text-label-tertiary",
            )}
          >
            <span className="min-w-0 flex-1 font-medium text-label">
              {value ? formatIsoDateShort(value) : "Datum wählen"}
            </span>
            <Calendar className="eh-date-field__icon" aria-hidden />
          </span>
        </button>
        {calendarOpen && !disabled ? (
          <BookingDateCalendar
            className="eh-date-calendar--popover"
            value={value}
            min={min}
            max={max}
            labelledBy={labelId}
            onChange={handleCalendarSelect}
          />
        ) : null}
      </div>
    </label>
  );
}
